import { BookOpen, FlaskConical, ClipboardCheck, Library, GraduationCap, CalendarRange } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { PROGRAMS, SUBJECT_GROUPS, ACADEMIC_CALENDAR } from '@/data/content';

const PROGRAM_STYLES = {
  crimson: 'bg-crimson text-white',
  gold: 'bg-gold text-navy',
  navy: 'bg-navy text-cream',
};

const Academics = () => (
  <>
    <PageHero eyebrow="Academics" title="Learning that builds for life." description="A structured, caring academic programme from Nursery to Class X — UP Board aligned, enriched with smart classes, labs and weekly assessments." />

    <section className="py-24 sm:py-32" data-testid="programs-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="01" eyebrow="Academic Programmes" title="Every stage, thoughtfully designed" />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.stage} delay={i * 0.08}>
              <div data-testid={`program-${i}`} className="group h-full rounded-lg border border-navy/10 bg-white p-8 transition-all hover:-translate-y-1 hover:border-gold">
                <div className="flex items-center justify-between">
                  <span className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] ${PROGRAM_STYLES[p.color]}`}>{p.classes}</span>
                  <GraduationCap className="h-6 w-6 text-gold" />
                </div>
                <h3 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-navy">{p.stage}</h3>
                <p className="mt-3 text-navy/70 leading-relaxed">{p.focus}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-cream-dark py-24 sm:py-32" data-testid="subjects-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="02" eyebrow="Classes & Subjects" title="A balanced curriculum" description="UP Board aligned curriculum with equal weight to languages, sciences, humanities and life skills." />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SUBJECT_GROUPS.map((g, i) => (
            <Reveal key={g.group} delay={i * 0.08}>
              <div className="h-full rounded-lg bg-white border border-navy/10 p-6">
                <h3 className="font-display text-lg font-bold text-crimson">{g.group}</h3>
                <ul className="mt-4 space-y-2.5">
                  {g.items.map((s) => (
                    <li key={s} className="flex items-center gap-2.5 text-sm font-medium text-navy/80">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 sm:py-32" data-testid="exam-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="03" eyebrow="Examination System" title="Measured, fair, stress-free" />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[
            { Icon: ClipboardCheck, title: 'Continuous Assessment', text: 'Unit tests every term, weekly class tests for senior classes, and regular homework checks keep learning consistent.' },
            { Icon: FlaskConical, title: 'Term Examinations', text: 'Half-yearly and annual examinations on the UP Board pattern, with practicals and viva for senior classes.' },
            { Icon: BookOpen, title: 'Progress Reports', text: 'Detailed report cards after every term, discussed personally with parents at Parent–Teacher Meetings.' },
          ].map(({ Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.1}>
              <div className="h-full rounded-lg border border-navy/10 bg-white p-8 transition-all hover:-translate-y-1 hover:border-gold">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-gold"><Icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-xl font-bold text-navy">{title}</h3>
                <p className="mt-3 text-sm text-navy/70 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-navy py-24 sm:py-32 noise-overlay" data-testid="calendar-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading dark number="04" eyebrow="Academic Calendar" title="The year at a glance" />
        <div className="mt-14 grid gap-px overflow-hidden rounded-lg border border-cream/10 bg-cream/10 sm:grid-cols-2 lg:grid-cols-5">
          {ACADEMIC_CALENDAR.map((m, i) => (
            <Reveal key={m.month} delay={i * 0.05} className="h-full">
              <div data-testid={`calendar-month-${i}`} className="flex h-full flex-col bg-navy p-6 transition-colors hover:bg-navy-light">
                <p className="flex items-center gap-2 font-display text-lg font-bold text-gold"><CalendarRange className="h-4 w-4" />{m.month}</p>
                <p className="mt-3 text-sm text-cream/60 leading-relaxed">{m.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 sm:py-32" data-testid="resources-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="05" eyebrow="Learning Resources" title="Tools that make learning stick" />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { Icon: Library, title: 'Library Period', text: 'A weekly library period for every class builds the reading habit early.' },
            { Icon: BookOpen, title: 'Practice Worksheets', text: 'Chapter-wise worksheets and revision notes before every exam.' },
            { Icon: FlaskConical, title: 'Lab Learning', text: 'Every science chapter ends with a hands-on lab activity.' },
            { Icon: GraduationCap, title: 'Doubt Classes', text: 'Free after-school doubt sessions for Classes IX & X before board exams.' },
          ].map(({ Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="h-full rounded-lg bg-cream-dark border border-navy/10 p-6 transition-all hover:-translate-y-1 hover:border-gold">
                <Icon className="h-7 w-7 text-crimson" />
                <h3 className="mt-4 font-display text-lg font-bold text-navy">{title}</h3>
                <p className="mt-2 text-sm text-navy/70 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Academics;
